/** @jsx jsx */
import { gql } from '@apollo/client';
import { Alert, Button, Elevation, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { css, jsx } from '@emotion/core';
import { Fragment, useState } from 'react';
import { ContentCard, ContentCardHeader } from '../../../components/ContentCard';
import ErrorCard from '../../../components/ErrorCard';
import LoadingCard from '../../../components/LoadingCard';
import NotFoundCard from '../../../components/NotFoundCard';
import {
  PropertyList,
  PropertyListDivider,
  PropertyListHeader,
  PropertyListItem,
} from '../../../components/PropertyList';
import { useBranchDetailQuery, useRegenerateApiKeyMutation } from '../../../generated/graphql';
import { getPersisted } from '../../../helpers/constants';
import SettingsDetailMenu from '../components/SettingsDetailMenu';
import SettingsEditOverlay from '../components/SettingsEditOverlay';

const SettingsDetailPage = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isRegenerateAlertOpen, setIsRegenerateAlertOpen] = useState(false);
  const { data, loading, error } = useBranchDetailQuery({
    variables: {
      branchId: getPersisted('branch_id'),
    },
  });
  const [regenerateApiKey, { loading: regenerating }] = useRegenerateApiKeyMutation({
    variables: {
      branchId: getPersisted('branch_id'),
    },
  });

  if (loading) return <LoadingCard />;
  if (error) return <ErrorCard />;
  if (!data?.branch) return <NotFoundCard resource="Einstellungen" />;

  const { branch } = data;

  return (
    <Fragment>
      <Alert
        isOpen={isRegenerateAlertOpen}
        cancelButtonText="Abbrechen"
        confirmButtonText="Regenerieren"
        icon={IconNames.REFRESH}
        intent={Intent.DANGER}
        onClose={() => setIsRegenerateAlertOpen(false)}
        onConfirm={regenerateApiKey as any}
      >
        <p>
          Soll der API Key wirklich regeneriert werden? <br />
          Alle API Abfragen, die noch den alten API Key verwerden, werden fehlschlagen
        </p>
      </Alert>
      <SettingsEditOverlay isOpen={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} />
      <ContentCard elevation={Elevation.TWO} css={styles.spacedCard}>
        <ContentCardHeader
          leftElement={<span css={styles.heading}>Einstellungen</span>}
          rightElement={<SettingsDetailMenu onEdit={() => setIsEditDialogOpen(true)} />}
        />
        <div css={styles.content}>
          <PropertyList>
            <PropertyListHeader>API</PropertyListHeader>
            <PropertyListItem label="API (v2) Instance">
              <span>{branch.slug}</span>
            </PropertyListItem>
            <PropertyListItem label="Legacy API (v1) Key">
              <span>{branch.api_key}</span>
              <Button
                small
                text="Regenerieren"
                icon={IconNames.REFRESH}
                intent={Intent.DANGER}
                onClick={() => setIsRegenerateAlertOpen(true)}
                loading={regenerating}
              />
            </PropertyListItem>
          </PropertyList>
          <PropertyListDivider />
          <PropertyList>
            <PropertyListHeader>Webhooks</PropertyListHeader>
            <PropertyListItem
              label="Veranstaltungsliste"
              value={branch.redirect_events || '—'}
              showValueDisabled={!branch.redirect_events}
            />
            <PropertyListItem
              label="Rückmeldung erfolgreich"
              value={branch.redirect_ticket_accept_ok || '—'}
              showValueDisabled={!branch.redirect_ticket_accept_ok}
            />
            <PropertyListItem
              label="Rückmeldung nicht gefunden"
              value={branch.redirect_ticket_accept_not_found || '—'}
              showValueDisabled={!branch.redirect_ticket_accept_not_found}
            />
            <PropertyListItem
              label="Passwort Zurücksetzen"
              value={branch.redirect_password_reset || '—'}
              showValueDisabled={!branch.redirect_password_reset}
            />
          </PropertyList>
        </div>
      </ContentCard>
    </Fragment>
  );
};

SettingsDetailPage.fragments = {
  settings: gql`
    fragment SettingsDetailPage on Branch {
      id
      slug
      api_key
      redirect_events
      redirect_ticket_accept_ok
      redirect_ticket_accept_not_found
      redirect_password_reset
    }
  `,
};

export default SettingsDetailPage;

const styles = {
  heading: css`
    font-size: 16px;
  `,
  content: css`
    display: flex;
    padding: 16px 20px;
  `,
  spacedCard: css`
    margin-bottom: 20px;
  `,
};
